import {
  curatedResearchCards,
  infectionExamples,
  medicineScenarios,
} from "../domain/health";
import type { ChallengeDefinition } from "../domain/schema";
import { shuffleAnswerPresentation } from "./presentationOrder";

interface Options {
  panel: HTMLElement;
  onComplete(challenge: ChallengeDefinition, hintsUsed: number): void;
  onClose(): void;
  speak(text: string): void;
}

export class HealthChallengeController {
  private challenge?: ChallengeDefinition;
  private hintsUsed = 0;
  private scenarioIndex = 0;

  constructor(private readonly options: Options) {}

  open(challenge: ChallengeDefinition): void {
    this.challenge = challenge;
    this.hintsUsed = 0;
    this.scenarioIndex = 0;
    this.options.panel.hidden = false;
    this.options.panel.innerHTML = `
      <div class="challenge-heading">
        <div><span class="pill">Healing Habitat safe-care station</span><h2 id="challenge-title">${challenge.title}</h2></div>
        <button class="icon-button" data-action="close" type="button" aria-label="Return to the habitat">×</button>
      </div>
      <div class="health-notice"><strong>General science—not medical advice</strong><span>Never handle or choose real medicine here. A responsible adult or health professional makes real care decisions.</span></div>
      <div id="challenge-body" class="challenge-body"></div>
      <div class="hint-row">
        <button class="secondary-button secondary-button--compact" data-action="hint" type="button">Need a hint?</button>
        <p id="hint-text" class="hint-text" aria-live="polite">Use the trusted guidance and supplied evidence. You can always ask for help.</p>
      </div>
      <output id="challenge-feedback" class="challenge-feedback" aria-live="assertive"></output>
    `;
    this.options.panel
      .querySelector("[data-action='close']")
      ?.addEventListener("click", () => this.close());
    this.options.panel
      .querySelector("[data-action='hint']")
      ?.addEventListener("click", () => this.showHint());
    if (challenge.id === "health.trusted-care-desk")
      this.renderMedicineScenario();
    else if (challenge.id === "health.forms-and-claims") this.renderForms();
    else if (challenge.id === "health.gentle-germ-trail")
      this.renderGermTrail();
    else if (challenge.id === "health.prevention-garden")
      this.renderPrevention();
    else this.renderResearch();
    shuffleAnswerPresentation(this.options.panel);
    this.options.panel
      .querySelector<HTMLButtonElement>("[data-action='close']")
      ?.focus();
  }

  close(): void {
    this.options.panel.hidden = true;
    this.options.panel.innerHTML = "";
    this.options.onClose();
  }

  private body(): HTMLElement {
    const body =
      this.options.panel.querySelector<HTMLElement>("#challenge-body");
    if (!body) throw new Error("Challenge body missing");
    return body;
  }

  private renderMedicineScenario(): void {
    const scenario = medicineScenarios[this.scenarioIndex];
    if (!scenario) {
      this.finish();
      return;
    }
    this.body().innerHTML = `
      <div class="health-step"><span>Safe-care card ${this.scenarioIndex + 1} of ${medicineScenarios.length}</span></div>
      <p class="challenge-question">${scenario.prompt}</p>
      <div class="choice-grid choice-grid--stacked">${scenario.options.map((option) => `<button type="button" data-medicine-option="${option.id}" data-safe="${option.safe}">${option.label}</button>`).join("")}</div>
    `;
    shuffleAnswerPresentation(this.body());
    for (const button of this.body().querySelectorAll<HTMLButtonElement>(
      "[data-medicine-option]",
    )) {
      button.addEventListener("click", () => {
        if (button.dataset.safe !== "true") {
          this.feedback(scenario.unsafeExplanation);
          button.classList.add("try-again");
          return;
        }
        this.feedback(scenario.safeExplanation, true);
        this.scenarioIndex += 1;
        window.setTimeout(() => this.renderMedicineScenario(), 250);
      });
    }
  }

  private renderForms(): void {
    const forms = [
      ["liquid", "Liquid"],
      ["tablet", "Tablet"],
      ["cream", "Cream"],
      ["drops", "Drops"],
      ["inhaled", "Inhaled form"],
    ];
    this.body().innerHTML = `
      <p>Observe common medicine forms without choosing or handling any real medicine.</p>
      <div class="medicine-form-grid" aria-label="Illustrated medicine forms">${forms.map(([id, label]) => `<div><span class="medicine-form medicine-form--${id}" aria-hidden="true"></span><strong>${label}</strong></div>`).join("")}</div>
      <form id="forms-form" class="safe-form">
        <label>What does a medicine's form tell us?<select id="form-meaning"><option value="">Choose</option><option value="appearance">How it is presented, not which illness it treats</option><option value="diagnosis">Exactly which illness someone has</option><option value="safe-anyone">That it is safe for anyone</option></select></label>
        <label>Which general statement is accurate?<select id="medicine-purpose"><option value="">Choose</option><option value="some">Medicines can treat illness, and some help prevent illness</option><option value="all">Every medicine treats every illness</option><option value="choose">A child should choose a form by themselves</option></select></label>
        <label>What keeps the decision safe?<select id="forms-safety"><option value="">Choose</option><option value="guidance">Responsible-adult or health-professional guidance and supplied instructions</option><option value="shape">The shape of the container</option><option value="friend">A friend's guess</option></select></label>
        <button class="primary-button" type="submit">Submit answers</button>
      </form>`;
    this.body()
      .querySelector<HTMLFormElement>("#forms-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const values = ["form-meaning", "medicine-purpose", "forms-safety"].map(
          (id) => this.body().querySelector<HTMLSelectElement>(`#${id}`)?.value,
        );
        const expected = ["appearance", "some", "guidance"];
        const messages = [
          "A form shows how medicine is presented. It cannot diagnose an illness or show what should be used.",
          "Medicines can help treat illness, and some help prevent illness. No medicine treats every illness.",
          "Only a responsible adult or health professional should guide medicine use, following its supplied label or instructions for amount and timing and never sharing it.",
        ];
        const wrong = values.findIndex(
          (value, index) => value !== expected[index],
        );
        if (wrong >= 0) {
          this.feedback(
            messages[wrong] ?? "Review the trusted guidance and try again.",
          );
          return;
        }
        this.finish();
      });
  }

  private renderGermTrail(): void {
    this.body().innerHTML = `
      <p>These are gentle fictional examples. For each living thing, follow the disclosed route—there are no graphic symptoms.</p>
      <form id="germ-form"><div class="infection-grid">${infectionExamples.map((example) => `<article><span class="living-icon" aria-hidden="true">${example.id === "plant" ? "🌻" : example.id === "animal" ? "🦔" : "🧑‍🌾"}</span><h3>${example.livingThing}</h3><p>${example.gentleProblem}</p><label>What does this show?<select data-infection="${example.id}"><option value="">Choose</option><option value="infectious">Some germs can enter and affect this kind of living thing</option><option value="never">Infectious disease can never affect this kind of living thing</option><option value="all">Every germ always causes disease</option></select></label></article>`).join("")}</div>
      <label class="unit-choice">Complete the general definition<select id="infection-definition"><option value="">Choose</option><option value="correct">An infectious disease is illness caused when certain germs enter and affect a living thing</option><option value="all-germs">Every germ is harmful</option><option value="only-people">Only people can have infectious disease</option></select></label>
      <button class="primary-button" type="submit">Submit answers</button></form>`;
    this.body()
      .querySelector<HTMLFormElement>("#germ-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const examplesCorrect = [
          ...this.body().querySelectorAll<HTMLSelectElement>(
            "[data-infection]",
          ),
        ].every((select) => select.value === "infectious");
        const definition = this.body().querySelector<HTMLSelectElement>(
          "#infection-definition",
        )?.value;
        if (!examplesCorrect) {
          this.feedback(
            "Some infectious diseases can affect plants, animals, and people. Each disease has particular hosts and routes; these cards do not say every germ is harmful.",
          );
          return;
        }
        if (definition !== "correct") {
          this.feedback(
            "An infectious disease is illness caused when certain germs enter and affect a living thing. Not every germ is harmful.",
          );
          return;
        }
        this.finish();
      });
  }

  private renderPrevention(): void {
    this.body().innerHTML = `
      <p>Match each disclosed route with a calm, general prevention action. These are not personal treatment choices.</p>
      <form id="prevention-form"><div class="prevention-list">${infectionExamples.map((example) => `<label><strong>${example.livingThing}</strong><span>${example.gentleProblem}</span><select data-prevention="${example.id}"><option value="">Choose</option><option value="${example.id}">${example.prevention}</option><option value="ignore">Ignore all hygiene because the problem is fictional</option><option value="medicine">Choose medicine without an adult</option></select></label>`).join("")}</div>
      <label class="unit-choice">What can vaccination do?<select id="vaccination-claim"><option value="">Choose</option><option value="some">Prevent some diseases in people and animals under professional guidance</option><option value="all">Prevent every disease</option><option value="treat">Treat any illness already present</option></select></label>
      <button class="primary-button" type="submit">Submit answers</button></form>`;
    this.body()
      .querySelector<HTMLFormElement>("#prevention-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const preventionCorrect = [
          ...this.body().querySelectorAll<HTMLSelectElement>(
            "[data-prevention]",
          ),
        ].every((select) => select.value === select.dataset.prevention);
        if (!preventionCorrect) {
          this.feedback(
            "Match prevention to the disclosed route: adult-guided tool cleaning, handwashing after animal care, or cough and hand hygiene. Do not choose medicine.",
          );
          return;
        }
        if (
          this.body().querySelector<HTMLSelectElement>("#vaccination-claim")
            ?.value !== "some"
        ) {
          this.feedback(
            "Vaccination can prevent some diseases in people and animals under professional guidance. It does not prevent every disease or treat an illness already present.",
          );
          return;
        }
        this.finish();
      });
  }

  private renderResearch(): void {
    this.body().innerHTML = `
      <p>Research stays inside these curated source cards. There are no open-web links and no questions about anyone's health.</p>
      <div class="source-card-grid">${curatedResearchCards.map((card) => `<article><span class="source-seal" aria-hidden="true">✓</span><h3>${card.organisation}</h3><p>${card.fact}</p></article>`).join("")}</div>
      <form id="research-form" class="safe-form">
        <label>Choose a focused, answerable question<select id="research-question"><option value="">Choose</option><option value="focused">How can vaccination prevent some diseases in people and animals?</option><option value="personal">Which vaccination should I personally have?</option><option value="private">What illnesses has my friend had?</option></select></label>
        <label>Which card supports the people claim?<select id="people-source"><option value="">Choose</option><option value="who">World Health Organization</option><option value="friend">An anonymous message</option></select></label>
        <label>Which card supports the animal claim?<select id="animal-source"><option value="">Choose</option><option value="woah">World Organisation for Animal Health</option><option value="guess">A guess from a game character</option></select></label>
        <label>Remember Skeleton Safari: what protects the heart and lungs?<select id="health-retrieval"><option value="">Choose</option><option value="ribs">Rib cage</option><option value="fur">Fur</option><option value="muscle">One muscle</option></select></label>
        <button class="primary-button" type="submit">Submit answers</button>
      </form>`;
    this.body()
      .querySelector<HTMLFormElement>("#research-form")
      ?.addEventListener("submit", (event) => {
        event.preventDefault();
        const answers = [
          this.body().querySelector<HTMLSelectElement>("#research-question")
            ?.value,
          this.body().querySelector<HTMLSelectElement>("#people-source")?.value,
          this.body().querySelector<HTMLSelectElement>("#animal-source")?.value,
          this.body().querySelector<HTMLSelectElement>("#health-retrieval")
            ?.value,
        ];
        const expected = ["focused", "who", "woah", "ribs"];
        if (!answers.every((answer, index) => answer === expected[index])) {
          this.feedback(
            "Choose the focused non-personal question, match each claim to its named trustworthy organisation, and remember that the rib cage protects the heart and lungs.",
          );
          return;
        }
        this.finish();
      });
  }

  private feedback(message: string, success = false): void {
    const output = this.options.panel.querySelector<HTMLOutputElement>(
      "#challenge-feedback",
    );
    if (!output) return;
    output.textContent = message;
    output.classList.toggle("challenge-feedback--success", success);
    if (success || this.hintsUsed > 0) this.options.speak(message);
  }

  private showHint(): void {
    const challenge = this.challenge;
    if (!challenge) return;
    this.hintsUsed = Math.min(2, this.hintsUsed + 1);
    const hint = challenge.hints[this.hintsUsed - 1] ?? challenge.hints[1];
    const text = this.options.panel.querySelector<HTMLElement>("#hint-text");
    if (text) text.textContent = hint;
    this.options.speak(hint);
    const button = this.options.panel.querySelector<HTMLButtonElement>(
      "[data-action='hint']",
    );
    if (button) {
      button.textContent =
        this.hintsUsed === 1 ? "Show a bigger hint" : "Hint shown";
      button.disabled = this.hintsUsed === 2;
    }
  }

  private finish(): void {
    const challenge = this.challenge;
    if (!challenge || this.body().querySelector(".finish-button")) return;
    this.feedback(challenge.successExplanation, true);
    const button = document.createElement("button");
    button.type = "button";
    button.className = "primary-button finish-button";
    button.textContent = "Light this Safe-Care Lantern";
    button.addEventListener("click", () => {
      this.options.onComplete(challenge, this.hintsUsed);
      this.close();
    });
    this.body().append(button);
    button.focus();
  }
}
